import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Modal, Text, Image } from '@vinted/web-ui';
import { clickEvent } from 'libs/common/event-tracker/events';
import { formatCurrencyAmount } from 'libs/utils/formatString';
import { navigateToPage } from 'libs/utils/window';
import { getItem, getTip } from 'data/api';
import { getItemThumbnail } from 'data/utils/item';
import { getLocale } from 'state/intl/selectors';
import { getUserId } from 'state/session/selectors';
import { nextPromoAfterItemUpload } from 'state/profile/actions';
import { getUploadedItemId } from 'state/profile/selectors';
import { WITHOUT_BRAND_ID } from 'state/item-upload/constants';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import { STATIC_ASSETS_URL } from 'constants/index';
import { ITEM_UPLOAD_URL } from 'constants/routes';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { ItemThumbnailSize } from 'constants/images';
import { transformCurrencyAmountDto } from 'data/transformers/currency-amount';
import ItemBox from 'components/ItemBox';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BACKGROUND_IMAGE = "".concat(STATIC_ASSETS_URL, "/assets/list-promotion/background.jpg");

const ListPromotionModal = () => {
  const translate = useTranslate();
  const {
    track
  } = useTracking();
  const dispatch = useDispatch();
  const locale = useSelector(getLocale);
  const userId = useSelector(getUserId);
  const newItemId = useSelector(getUploadedItemId);
  const {
    0: item,
    1: setItem
  } = useState();
  const {
    0: isModalOpen,
    1: setIsModalOpen
  } = useState(false);
  const {
    0: tipBody,
    1: setTipBody
  } = useState('');
  const {
    0: tipTitle,
    1: setTipTitle
  } = useState('');
  const fetchItem = useCallback(async itemId => {
    const response = await getItem({
      id: itemId
    });

    if ('errors' in response) {
      dispatch(nextPromoAfterItemUpload());
      return;
    }

    if (!response.item) {
      dispatch(nextPromoAfterItemUpload());
      return;
    }

    setItem({
      id: response.item.id,
      brand: response.item.brand_id === WITHOUT_BRAND_ID ? '' : response.item.brand,
      size: response.item.size,
      price: formatCurrencyAmount(transformCurrencyAmountDto(response.item.price), locale),
      image: getItemThumbnail(response.item.photos, ItemThumbnailSize.Large)
    });
  }, [dispatch, locale]);
  const fetchTip = useCallback(async () => {
    if (!userId) {
      dispatch(nextPromoAfterItemUpload());
      return;
    }

    const response = await getTip(userId, 'upload_more');

    if ('errors' in response) {
      dispatch(nextPromoAfterItemUpload());
      return;
    }

    if (response.tip) {
      setTipBody(response.tip.body);
      setTipTitle(response.tip.title);
      setIsModalOpen(true);
    } else {
      dispatch(nextPromoAfterItemUpload());
    }
  }, [dispatch, userId]);
  useEffect(() => {
    if (!newItemId) {
      dispatch(nextPromoAfterItemUpload());
      return;
    }

    fetchTip();
    fetchItem(newItemId);
  }, [dispatch, fetchTip, fetchItem, newItemId]);

  const trackClickEvent = target => {
    track(clickEvent({
      target
    }));
  };

  const onAcceptButtonClick = () => {
    trackClickEvent(ClickableElement.UploadMoreAccept);
    navigateToPage(ITEM_UPLOAD_URL);
  };

  const onSkipButtonClick = () => {
    trackClickEvent(ClickableElement.UploadMoreCancel);
    setIsModalOpen(false);
    dispatch(nextPromoAfterItemUpload());
  };

  const renderItemBox = () => {
    if (!item) return null;
    return /*#__PURE__*/_jsxs("div", {
      className: "u-position-relative",
      children: [/*#__PURE__*/_jsx(Image, {
        src: BACKGROUND_IMAGE
      }), /*#__PURE__*/_jsx("div", {
        className: "list-promotion__item-wrapper",
        children: /*#__PURE__*/_jsx(Card, {
          styling: Card.Styling.Elevated,
          children: /*#__PURE__*/_jsxs("div", {
            className: "list-promotion__item-box",
            children: [/*#__PURE__*/_jsx(ItemBox, {
              id: item.id,
              image: item.image,
              price: item.price,
              url: "",
              description: {
                title: item.brand,
                subtitle: item.size
              },
              favourite: {
                count: 0,
                favourited: false
              }
            }), /*#__PURE__*/_jsx("div", {
              className: "list-promotion__overlay"
            })]
          })
        })
      })]
    });
  };

  return /*#__PURE__*/_jsx(Modal, {
    show: !!item && isModalOpen,
    children: /*#__PURE__*/_jsxs("div", {
      className: "list-promotion",
      children: [renderItemBox(), /*#__PURE__*/_jsxs("div", {
        className: "c-modal__inline-content",
        children: [/*#__PURE__*/_jsx("div", {
          className: "c-modal__title",
          children: /*#__PURE__*/_jsx(Text, {
            text: tipTitle,
            type: Text.Type.Heading
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "c-modal__body",
          children: /*#__PURE__*/_jsx(Text, {
            text: tipBody
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "c-modal__actions",
        children: [/*#__PURE__*/_jsx("div", {
          className: "c-modal__action",
          children: /*#__PURE__*/_jsx(Button, {
            text: translate('list_promotion.submit_cta'),
            onClick: onAcceptButtonClick,
            styling: Button.Styling.Filled,
            size: Button.Size.Medium
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "c-modal__action",
          children: /*#__PURE__*/_jsx(Button, {
            text: translate('list_promotion.cancel_cta'),
            onClick: onSkipButtonClick,
            styling: Button.Styling.Flat,
            size: Button.Size.Medium
          })
        })]
      })]
    })
  });
};

export default ListPromotionModal;